/**
 * The collision list, and the bodies asleep in it.
 *
 * Every box here is hashed through `solidColliders()`, boot split and all.
 * The order matters as much as the contents: the fingerprint hashes the array
 * as it stands, so re-sorting it retires every leaderboard time.
 */

import { solid, type SleeperSpec, type SolidSpec } from '../world/Level';
import { BARN_H, T, WALL_H } from './levelDimensions';
import {
  ANSALANKA,
  ANSALANKA_CAMPER,
  ANSALANKA_HARKOT,
  ANSALANKA_ROW,
  BEMARI_Z,
  CABLE_X,
  TOLPPA_BACK_Z,
  TOLPPA_Z,
} from './levelAnsalanka';
import { LAN_EAST_WALL, LAN_HALL_EXTENSION, buildTables } from './levelJuhlasali';
import { KIERTOKALUSTO } from './levelLeiri';
import {
  KIMMON_YOPOYTA_X,
  KIMMO_BED_X,
  NUKKUJAN_YOPOYTA_X,
  ROOMMATE,
  ROOMMATE_BED_X,
  bedFrame,
  yopoyta,
} from './levelMakuuhuone';
import {
  VAATEKAAPPI,
} from './levelPirtinKaapit';
import { MAYSA } from './levelMuurikka';
import { ETEINEN, RISTEYS, eteisenLansiseina, wcValiseinat } from './levelNavetanOvi';
import { KAMIINA, KLAPITELINE, PALJU, POYTARYHMA, VERAJA, tubRim } from './levelPalju';
import { CAMPERS, PARKED } from './levelParkkipaikka';
import {
  AIDAN_POHJOISPAA,
  AITAUS_KETJU,
  KUUSIKKO_ANSALANKA,
  KUUSIKKO_KAARRE,
  KUUSIKKO_PALJU,
  KUUSIKKO_PARKIN_PIELI,
  MAATILA,
} from './levelPiha';
import {
  KAYTAVA,
  KAYTAVAN_VARUSTEET,
  PIRTTI_ROUTE,
  TAKAPIHA_ESTEET,
  kaytavanSangyt,
  pirtinEtelaseina,
} from './levelPirtti';
import { PORUKKA, POYTAPORUKKA, TORNI } from './levelPorukka';
import { TELTAN_IHMISET, XLMOTO_TELTTA } from './levelTeltta';

/* ================================================================ solids == */

export const solids: SolidSpec[] = [
  ...KIERTOKALUSTO,
  /* ---- pihan aidat (keeps the player inside the blockout) ---- */
  solid('aita-pohjoinen', 'fence', -1.3, -51.3, 180.3, -51.0, 2.6),
  solid('aita-etela', 'fence', -1.3, 20.0, 180.3, 20.3, 2.6),
  solid('aita-lansi', 'fence', -1.3, -51.3, -1.0, 20.3, 2.6),
  solid('aita-ita', 'fence', 180.0, -51.3, 180.3, 20.3, 2.6),

  /* ---- EINON PIRTTI: makuuhuone ---- */
  solid('pirtti-seina-lansi', 'pirttiWall', -T, -6.3, 0, 4.3, WALL_H),
  // The south wall, with the window cut out of it. See `IKKUNA`.
  ...pirtinEtelaseina(),
  solid('pirtti-seina-pohjoinen', 'pirttiWall', -T, 4.0, 10.3, 4.3, WALL_H),
  solid('pirtti-vali-a', 'pirttiWall', 10.0, -6.3, 10.3, PIRTTI_ROUTE.arch.minZ, WALL_H),
  // Runs past the bedroom's own north wall, because the store wing is wider
  // than the cabin it is attached to: stopping this at 4.3 would leave the
  // käytävä a player-wide slot straight out into the back field.
  solid('pirtti-vali-b', 'pirttiWall', 10.0, PIRTTI_ROUTE.arch.maxZ, 10.3, KAYTAVA.northZ + T, WALL_H),

  /* ---- EINON PIRTTI: käytävä ---- */
  solid('kaytava-seina-etela', 'pirttiWall', 10.0, 0.1, 33.3, 0.4, WALL_H),
  // Derived, so the corridor's width cannot drift away from the beds fitted
  // into it - `KAYTAVA` is what `kaytavanSankyBox` measures the clear side off.
  solid('kaytava-seina-pohjoinen', 'pirttiWall', 10.0, KAYTAVA.northZ, 33.3, KAYTAVA.northZ + T, WALL_H),
  solid('kaytava-paaty-a', 'pirttiWall', 33.0, 0.1, 33.3, 0.9, WALL_H),
  solid('kaytava-paaty-b', 'pirttiWall', 33.0, 2.6, 33.3, KAYTAVA.northZ + T, WALL_H),

  /* ---- makuuhuoneen kamat ---- */
  bedFrame('sanky-kimmo', KIMMO_BED_X),
  bedFrame('sanky-nukkuja', ROOMMATE_BED_X),
  // One in each south corner, at the head of the bed it belongs to. There used
  // to be a single one of these against the west wall a metre past the foot of
  // Kimmo's bed, which is a side table rather than a bedside one: nobody in
  // either bed could reach it, and it stood in the open where the beds do not.
  yopoyta('kimmo', KIMMON_YOPOYTA_X),
  yopoyta('nukkuja', NUKKUJAN_YOPOYTA_X),
  // Against the north wall: the east wall is the second bed's now.
  solid('tyopoyta', 'furniture', 6.8, 2.6, 8.8, 4.0, 0.75),
  solid('vaatekasa', 'fabric', 0.2, -0.8, 1.6, 0.6, 0.45),
  solid('kaljakori', 'furniture', 0.2, 1.0, 1.0, 1.8, 0.5),
  // Derived, because the lipasto beside it and the seinäkaappi over it are
  // both placed off this box. See `VAATEKAAPPI`.
  solid(
    'vaatekaappi',
    'furniture',
    VAATEKAAPPI.fromX,
    VAATEKAAPPI.frontZ,
    VAATEKAAPPI.toX,
    VAATEKAAPPI.backZ,
    VAATEKAAPPI.height,
  ),

  /* ---- käytävän sängyt ja kaksi tarkkuusväliä ---- */
  ...kaytavanSangyt(),
  ...KAYTAVAN_VARUSTEET,

  /* ---- PIRTIN TAKAPIHA: mitä ikkunasta hypännyt kohtaa ---- */
  ...TAKAPIHA_ESTEET,

  /* ---- PARKKIPAIKKA ---- */
  ...PARKED,
  ...CAMPERS,
  // Along the edge of the approach, clear of the doorway and the tree trunks.
  solid('betoniporsas', 'stone', 35.5, 2.5, 37.1, 3.3, 0.6),
  // Abandoned off the running lane so the long bends can build momentum.
  solid('ostoskarry', 'metal', 35.5, -9.0, 36.7, -7.8, 0.95),

  /* ---- PIHA: reitin esteet parkkipaikan jälkeen ---- */
  XLMOTO_TELTTA,
  // In the pocket behind the row, where the way round the left has to come back
  // up. It moved there with the row: half a metre off the bemari's flank, which
  // is not a lane, so the cable is hopped and then the grill is gone round
  // rather than the two being one decision.
  solid('grilli', 'metal', 110.6, -21.4, 112.4, -19.6, 1.1),

  /* ---- ANSALANKA: pihalle parkattu rivi, ja bemarin latausjohto ---- */
  ...ANSALANKA_ROW,
  ...ANSALANKA_HARKOT,
  ANSALANKA_CAMPER,
  // Dumped against the end of the row, where it makes going round the
  // right-hand side of the whole thing that bit less worth it.
  solid(
    'kottikarryt',
    'metal',
    ANSALANKA_CAMPER.box.minX,
    ANSALANKA_CAMPER.box.maxZ,
    ANSALANKA_CAMPER.box.minX + 2.0,
    ANSALANKA_CAMPER.box.maxZ + 2.5,
    0.9,
  ),
  solid(
    'ansalanka-latauspiste',
    'metal',
    CABLE_X - 0.2,
    TOLPPA_BACK_Z,
    CABLE_X + 0.2,
    TOLPPA_Z,
    1.15,
  ),
  // The ansalanka itself. Both ends are buried in something solid on purpose:
  // a cable you can run round the end of is scenery, not an obstacle.
  {
    ...solid(
      'ansalanka-latausjohto',
      'cable',
      CABLE_X - ANSALANKA.cableHalfWidth,
      TOLPPA_Z - 0.2,
      CABLE_X + ANSALANKA.cableHalfWidth,
      BEMARI_Z + 0.175,
      ANSALANKA.cableHeight,
      ANSALANKA.cableY,
    ),
    shape: 'cable',
  },

  /* ---- PALJU JUHLANAVETAN EDESSÄ. Hannu asuu täällä. ---- */
  ...tubRim('palju', PALJU.x, PALJU.z, PALJU.radius, PALJU.rim),
  // The kamiina itself. A collider, unlike the one that used to sit in the
  // water: it stands in the open on the far side of the rim now, and a stove
  // you can run through is not a stove. Squared off around the drum, because
  // collision here is axis-aligned and a firebox is mostly its cladding.
  solid(
    'kamiina',
    'stove',
    KAMIINA.x - KAMIINA.radius,
    KAMIINA.z - KAMIINA.radius,
    KAMIINA.x + KAMIINA.radius,
    KAMIINA.z + KAMIINA.radius,
    KAMIINA.height,
  ),
  // The fence that makes the palju a yard rather than a field, and the veräjä
  // in it. See VERAJA: shut until the fire has been fed, and the only hole in
  // the boards - the north end runs on into the kuusikko, which is where the
  // yard's second way out used to be.
  solid(
    'paljun-aita',
    'fence',
    VERAJA.x - 0.15,
    VERAJA.to,
    VERAJA.x + 0.15,
    AIDAN_POHJOISPAA,
    VERAJA.height,
  ),
  // South of the veräjä the fence is a stub that runs into the juhlanavetta's
  // own south wall, which is where the yard ends anyway.
  solid(
    'paljun-aidan-pieli',
    'fence',
    VERAJA.x - 0.15,
    -29.8,
    VERAJA.x + 0.15,
    VERAJA.from,
    VERAJA.height,
  ),
  {
    ...solid('paljun-veraja', 'wood', VERAJA.x - 0.15, VERAJA.from, VERAJA.x + 0.15, VERAJA.to, VERAJA.height),
    // The leaf is built hinged by `World.buildVeraja` so it can actually swing.
    shape: 'gate',
  },
  // The south wall of the gate, and the winter's wood.
  solid('halkopino', 'wood', 118.0, -29.4, 125.6, -27.6, 1.6),
  // The klapiteline: the rack the kamiina is fed from, and the reason Hannu
  // wants you in the first place - '"Kimmo" hei, pist pari klapii!' is one log
  // off the top of this and in through the hatch on the far side of the tub.
  //
  // It stands on the tub's north rim, and that is what decides which way past
  // the palju is fast: the corridor between the rack and the kuusikko is the
  // only line the logs are within reach of. Flush against the rim, so there is
  // no slot behind it to get wedged into.
  ...KLAPITELINE.solids,

  // Land here before jumping over the fence. Both jumps
  // keep their normal momentum cost; the platform does not refund speed.
  solid('kaljalava', 'wood', 121.0, -19.4, 123.0, -17.9, 0.65),

  ...TELTAN_IHMISET,

  /* ---- JUHLANAVETAN ETUPIHA: lipputanko, pöytä, penkki, roska-astia ---- */
  //
  // Straight off the photograph of the place: the flagpole out in the open,
  // the picnic table against the wall where the sun hits it, a bench under the
  // window and the bin beside the porch. None of it is on the run line - it is
  // there so the yard reads as somewhere people are, rather than as a surface
  // between two doors.
  solid('lipputanko', 'porcelain', 141.7, -28.4, 141.9, -28.2, 9.0),
  solid('piknikpoyta', 'wood', 134.6, -29.05, 137.2, -28.25, 0.75),
  solid('piknikpenkki-a', 'wood', 134.6, -29.45, 137.2, -29.05, 0.45),
  solid('piknikpenkki-b', 'wood', 134.6, -28.25, 137.2, -27.85, 0.45),
  solid('penkki', 'wood', 143.5, -29.3, 145.3, -28.85, 0.45),

  /* ---- NAVETAN ETUPIHA: pitkä pöytä ja penkit ---- */
  //
  // Across the apron the veräjä opens onto, hard against the fence at its west
  // end. See PITKA_POYTA for why it is there and why it stays.
  ...POYTARYHMA.solids,
  // Moved off the palju's fence line and onto the kivetys by the porch, which
  // is where a bin lives anyway: the veräjä's pieli now runs where it stood.
  solid('roska-astia', 'metal', 131.7, -29.2, 132.4, -28.5, 1.1),

  /* ---- PIHA: rekvisiitta ---- */
  solid('aitat', 'wood', 46.0, 8.0, 54.0, 15.0, 2.8),
  solid('vaarin-pirtti', 'pirttiWall', 70.0, 6.0, 82.0, 15.0, 4.2),
  solid('puun-runko', 'wood', 66.0, 4.0, 67.2, 5.2, 4.5),
  // The lot's own clothesline. The second post used to stand eight metres out
  // in the grass with nothing between the two of them; the grass is the
  // neighbour's field now, so it has come in to where the line is a line.
  solid('pyykkinaru-tolppa-a', 'wood', 36.0, -18.0, 36.4, -17.6, 2.2),
  solid('pyykkinaru-tolppa-b', 'wood', 36.0, -14.0, 36.4, -13.6, 2.2),
  solid('kivikasa', 'metal', 92.0, 2.0, 95.0, 5.0, 0.8),

  /* ---- PIHAN VASEN LAITA: naapurin maatila ---- */
  //
  // The left-hand edge of the yard, and the reason the parkkipaikka has to be
  // run rather than walked round. See `AITAUS` above for the whole of it.
  ...AITAUS_KETJU,
  ...MAATILA,

  /* ---- PIHAN OIKEA LAITA: kuusikko ---- */
  //
  // The right-hand edge of the yard, and the reason neither obstacle in it can
  // be walked round any more. See `SPRUCE` above for the whole of it, and
  // `KUUSIKKO_PARKIN_PIELI` for the two ends it used to stop short of.
  ...KUUSIKKO_PARKIN_PIELI.trunks,
  ...KUUSIKKO_ANSALANKA.trunks,
  ...KUUSIKKO_PALJU.trunks,
  ...KUUSIKKO_KAARRE.trunks,

  /* =============== JUHLANAVETTA: ulkoseinät =============== */
  //
  // Outside walls carry their own material: rendered block under a brown metal
  // roof, which is what the building actually looks like from the yard. The
  // `lanWall` grey stays for everything you only ever see from inside.
  //
  // South face, with the entrance door gap at x[129.4 .. 130.9].
  solid('navetta-etela-a', 'barnWall', 125.65, -30.0, 129.4, -29.65, BARN_H),
  solid('navetta-etela-b', 'barnWall', 130.9, -30.0, 155.75, -29.65, BARN_H),
  solid('navetta-pohjoinen', 'barnWall', 125.65, -45.45, 155.75, -45.1, BARN_H),
  solid('navetta-lansi', 'barnWall', 125.65, -45.45, 126.0, -29.65, BARN_H),
  solid('navetta-ita', 'barnWall', 155.4, -45.45, 155.75, -29.65, BARN_H),

  /* ---- TORNIMAJOITUS: vanha rehusiilo itäpäädyssä ---- */
  //
  // Inscribed box for the round tower - the cylinder mesh is decor only. See
  // `TORNI`.
  solid(
    'tornimajoitus-runko',
    'tower',
    TORNI.x - TORNI.inscribed,
    TORNI.z - TORNI.inscribed,
    TORNI.x + TORNI.inscribed,
    TORNI.z + TORNI.inscribed,
    TORNI.height,
  ),
  // Dragged out of it and left facing the yard, which is what happens to a
  // sofa in a room somebody is only sleeping in.
  solid('vanha-sohva', 'fabric', TORNI.x - 1.6, -28.4, TORNI.x + 0.8, -26.4, 0.8),

  /* ---- ETEINEN: kapea käytävä, kivetyksen vasemmalla puolella ---- */
  // West wall in four pieces, because three WC doors are cut out of it.
  ...eteisenLansiseina(),
  // East wall, solid the whole way from the front door to the junction: the
  // hall does not open until all three WCs are behind you. `sali-lansi` picks
  // the same wall up again on the far side of the opening.
  solid('eteinen-ita', 'lanWall', 131.2, RISTEYS.saliFrom, 131.55, ETEINEN.frontZ, BARN_H),
  // The end wall, split around the doorway the corridor carries on through.
  solid(
    'eteinen-pohjoinen-lansi',
    'lanWall',
    126.0,
    ETEINEN.backZ - 0.35,
    RISTEYS.karaokeMinX,
    ETEINEN.backZ,
    BARN_H,
  ),
  solid(
    'eteinen-pohjoinen-ita',
    'lanWall',
    RISTEYS.karaokeMaxX,
    ETEINEN.backZ - 0.35,
    131.55,
    ETEINEN.backZ,
    BARN_H,
  ),

  /* ---- KARAOKE: pätkä käytävää oviaukon takana ---- */
  //
  // Two metres, and shut at the end. It exists so that the arrow over the
  // doorway is telling the truth - a corridor that carries on into a painted
  // wall is worse than no corridor at all. Behind the karaoke's own door is
  // the takkahuone, which is one of the rooms that is only a footprint.
  solid(
    'karaoke-kaytava-lansi',
    'lanWall',
    ETEINEN.westX,
    RISTEYS.karaokeEndZ,
    ETEINEN.westX + 0.35,
    ETEINEN.backZ - 0.35,
    BARN_H,
  ),
  solid(
    'karaoke-kaytava-ita',
    'lanWall',
    131.2,
    RISTEYS.karaokeEndZ,
    131.55,
    ETEINEN.backZ - 0.35,
    BARN_H,
  ),
  solid('karaoke-pera', 'lanWall', ETEINEN.westX, RISTEYS.karaokeEndZ - 0.35, 131.55, RISTEYS.karaokeEndZ, BARN_H),

  /* ---- ETEISEN VESSAT ---- */
  ...wcValiseinat(),

  /* ---- sisäänkäynnin katos ---- */
  solid('porstua-lansi', 'barnWall', 128.85, -30.0, 129.2, -27.4, 2.8),
  solid('porstua-ita', 'barnWall', 130.9, -30.0, 131.25, -27.4, 2.8),
  {
    // Mäysä, across the porch, until there are three makkaraa on the plate.
    // Invisible because the thing you can see is a man - `World` draws him out
    // of `aamupala.maysa` - and a barn-coloured slab here would be a wall with
    // somebody standing inside it. See the `MAYSA` block for why this is the
    // one person in the venue with a box.
    ...solid('maysa-ovella', 'barnWall', MAYSA.minX, MAYSA.minZ, MAYSA.maxX, MAYSA.maxZ, MAYSA.height),
    shape: 'invisible',
  },

  /* ---- JUHLASALI: seinät ---- */
  solid('sali-lansi', 'lanWall', 131.2, ETEINEN.backZ, 131.55, RISTEYS.saliTo, BARN_H),
  solid('sali-pohjoinen', 'lanWall', 131.2, -39.85, 155.75, -39.5, BARN_H),
  solid('sali-ita', 'lanWall', LAN_EAST_WALL, -39.85, LAN_EAST_WALL + 0.35, -29.65, BARN_H),

  {
    // The porukka, across the runway a stride past the glasses, until there is
    // something in the tuoppi. Invisible for the same reason Mäysä's box is:
    // what you can see there is seven people - `World` draws them out of
    // `sima.crowd` - and a lanWall-coloured slab would be a wall with a crowd
    // standing inside it. See the `PORUKKA` block for why it is this box.
    ...solid('porukka-tiella', 'lanWall', PORUKKA.minX, PORUKKA.minZ, PORUKKA.maxX, PORUKKA.maxZ, PORUKKA.height),
    shape: 'invisible',
  },
  {
    ...solid(POYTAPORUKKA.name, 'lanWall', POYTAPORUKKA.minX, POYTAPORUKKA.minZ,
      POYTAPORUKKA.maxX, POYTAPORUKKA.maxZ, POYTAPORUKKA.height),
    shape: 'invisible',
  },

  /* ---- suljetut takatilat, jotta rakennuksella on oikea muoto ---- */
  solid('takkahuone-vali', 'lanWall', 134.4, -45.45, 134.75, -39.5, BARN_H),
  solid('terassi-vali', 'lanWall', 145.2, -45.45, 145.55, -39.5, BARN_H),
  solid('sauna-vali', 'lanWall', LAN_EAST_WALL + 0.35, -38.6, 155.75, -38.25, BARN_H),
  solid('suihku-vali', 'lanWall', LAN_EAST_WALL + 0.35, -33.85, 155.75, -33.5, BARN_H),
  // Stops just short of the navetta's west wall rather than flush against it:
  // the WCs look straight at this face now, and two coplanar walls z-fight.
  solid('keittio', 'lanWall', 119.4, -36.9, 125.9, -32.2, 3.0),

  /* ---- JUHLASALI: pöydät ---- */
  ...buildTables(),
];

/* ================================================== nukkuvat pelaajat ==== */

/**
 * Every body you have to get past: the roommate in Einon pirtti, then the LAN
 * players passed out around the tables. Static, and identical on every run.
 */
export const sleepers: SleeperSpec[] = [
  // The one you woke up next to, still in his bed back in the pirtti.
  ROOMMATE,
  // The one who did not make it inside, laid out across the mouth of the porch.
  // He is the last thing between the yard and the door, and hopping him costs
  // vauhti at exactly the moment Hannu is closest - which is the point of him
  // being there rather than two metres to one side.
  { name: 'nukkuja-ovella', center: [130.05, -26.85], orientation: 'x', length: 1.9, width: 0.85, height: 0.42 },
  // The four down the middle of the runway, each one 0.2 m further north than
  // the last: `z = -33.425 .. -31.975` once the width of a man is counted in,
  // which is the middle 1.45 m of a 4.5 m room. The step is what makes the two
  // lanes trade width as the hall goes on rather than run parallel - see the
  // note over `TABLE_CENTERS` - and it is also just what four men who lay down
  // one after another in the same room would look like. Jumping them (they are
  // 0.42 m tall, the jump apex is 0.71 m) is still the fast answer, at the
  // price of what a jump costs the vauhti meter.
  //
  // They used to lie against the two long walls, alternating, and the room was
  // run down the middle between them. The baari is why they do not any more:
  // both halves of the sima errand are on the south wall in the first five
  // metres of the hall, `nukkuja-2` was asleep across the front of the
  // tuoppipöytä, and the one place on this level a player has to stand still
  // in was also the one with a man lying in it. Off the walls, the same four
  // bodies buy the hall two lines instead of one - see the note over
  // `TABLE_CENTERS` for what that is worth and what it cost.
  //
  // The x gaps are left as they were, and they are where a line crosses from
  // one side to the other: 1.1 m between the second and the third and again
  // between the third and the fourth, and 0.2 m between the first two, which
  // is not a gap at all.
  { name: 'nukkuja-1', center: [134.7, -32.4], orientation: 'x', length: 1.9, width: 0.85, height: 0.42 },
  { name: 'nukkuja-2', center: [136.8, -32.6], orientation: 'x', length: 1.9, width: 0.85, height: 0.42 },
  { name: 'nukkuja-3', center: [139.8, -32.8], orientation: 'x', length: 1.9, width: 0.85, height: 0.42 },
  { name: 'nukkuja-4', center: [142.8, -33.0], orientation: 'x', length: 1.9, width: 0.85, height: 0.42 },
  // The last one, guarding the corner into the corridor beside table 5. He
  // leaves a 1.48 m gap against the table and nothing at all on the wall side,
  // so the last turn of the run has exactly one clean line through it.
  { name: 'nukkuja-5', center: [146.9 + LAN_HALL_EXTENSION, -33.6], orientation: 'z', length: 1.9, width: 0.85, height: 0.42 },
  // The entry step stays; the last two aisles have no recovery step.
  { name: 'nukkuja-6', center: [134.6, -37.0], orientation: 'z', length: 1.9, width: 0.85, height: 0.42 },
];
